import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { BrandLogo } from "@/components/BrandLogo";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function NavItem({ item, onNavigate, collapsed }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = item.tKey ? t(`nav.${item.tKey}`, { defaultValue: item.label }) : item.label;
  const base = "group relative flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-200";
  const pad = collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2";

  const inner = !item.enabled ? (
    <div data-testid={`nav-${item.key}-disabled`} className={cn(base, pad, "cursor-not-allowed text-muted-foreground/40")}>
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
      {!collapsed && <><span className="flex-1 truncate">{label}</span>
        <Badge variant="outline" className="border-border/60 px-1.5 text-[9px] uppercase tracking-[0.15em]">{t("shell.off")}</Badge></>}
    </div>
  ) : (
    <NavLink to={item.to} end={item.end} onClick={onNavigate} data-testid={`nav-${item.key}`} aria-label={label}>
      {({ isActive }) => (
        <span className={cn(base, pad, isActive ? "bg-primary/[0.08] text-foreground" : "text-muted-foreground hover:bg-accent/70 hover:text-foreground")}>
          {isActive && <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
          <Icon className={cn("h-[18px] w-[18px] shrink-0 transition-colors", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} strokeWidth={1.6} />
          {!collapsed && <span className="flex-1 truncate">{label}</span>}
          {!collapsed && !item.built && (
            <Badge variant="outline" className="border-border/60 px-1.5 text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{t("shell.soon")}</Badge>
          )}
        </span>
      )}
    </NavLink>
  );

  if (!collapsed) return inner;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild><div>{inner}</div></TooltipTrigger>
      <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );
}

export function SidebarNav({ brand, sections, onNavigate, collapsed = false, onToggle }) {
  const { t } = useTranslation();
  return (
    <TooltipProvider>
      <div className="flex h-full flex-col bg-card" data-testid="sidebar-nav" data-collapsed={collapsed}>
        <div className={cn("flex items-center gap-3 border-b border-border/60 py-4", collapsed ? "justify-center px-2" : "px-4")}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg">
            {brand.logo ? <img src={brand.logo} alt={brand.title} className="h-full w-full object-contain" /> : <BrandLogo className="h-full w-full rounded-lg" />}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p data-testid="sidebar-brand-name" className="truncate font-display text-sm font-bold leading-tight tracking-tight">{brand.title}</p>
              <p className="truncate text-[11px] text-muted-foreground">{brand.subtitle}</p>
            </div>
          )}
        </div>

        <nav className={cn("flex flex-1 flex-col gap-6 overflow-y-auto py-5", collapsed ? "px-2" : "px-3")}>
          {sections.map((section) => (
            <div key={section.title} className="flex flex-col gap-0.5">
              {collapsed
                ? <div className="mx-2 mb-1 h-px bg-border/60 first:hidden" />
                : <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">{section.tKey ? t(`nav.${section.tKey}`, { defaultValue: section.title }) : section.title}</p>}
              {section.items.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />)}
            </div>
          ))}
        </nav>

        <div className={cn("flex items-center border-t border-border/60 py-3", collapsed ? "justify-center px-2" : "gap-2 px-4")}>
          {!collapsed && (
            <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden /> {t("shell.systemOperational")}
            </span>
          )}
          {onToggle && (
            <button type="button" onClick={onToggle} data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? t("shell.expandMenu") : t("shell.collapseMenu")}
              className={cn("rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground", !collapsed && "ml-auto")}>
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
